import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaOptionIcon, addFileIcon } from '@europrocurement/l2d-icons';
import {
    ContextActionType,
    LazyBreadcrumb,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { Chip, Typography } from '@mui/material';
import {
    documentTypes,
    findLabelByDocTypeId,
    FormaliteDocument,
    formalitiesDocumentSelector,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { FormalityAppDispatch } from '@europrocurement/l2d-domain/reducers/formalites/types';
import { useClickActions } from '@europrocurement/l2d-hooks';
import {
    ROLE_API_FORMALITE_TYPEDOC_CREATE,
    UseKeycloakService,
} from '@europrocurement/l2d-keycloak';
import DocumentTypeListItemActions from './DocumentTypeListItemActions';
import { DOCUMENT_TYPE_NEW } from '../../constants/paths';

const DocumentTypesListing: React.FunctionComponent = function () {
    const { getFormaliteDocuments } = useFormaliteService();
    const datasource = useSelector(formalitiesDocumentSelector);
    const navigate = useNavigate();
    const keycloakService = UseKeycloakService();
    const dispatch = useDispatch<FormalityAppDispatch>();
    const { navigateOnClick } = useClickActions({ navigate });

    const handleRowClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: FormaliteDocument) => {
            navigateOnClick({ path: `${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: FormaliteDocument) => {
            navigateOnClick({ path: `${id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const contextActions: ContextActionType[] = useMemo(() => {
        if (keycloakService.checkRole(ROLE_API_FORMALITE_TYPEDOC_CREATE)) {
            return [
                {
                    name: 'Ajouter un type de document',
                    icon: <FaOptionIcon {...addFileIcon.props} />,
                    action: () => navigate(DOCUMENT_TYPE_NEW),
                },
            ];
        }
        return [];
    }, [keycloakService, navigate]);

    React.useEffect(() => {
        dispatch(getFormaliteDocuments({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<FormaliteDocument>[] = useMemo(
        () => [
            {
                label: 'Libellé',
                render: ({ libelle }: FormaliteDocument) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {libelle}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'libelle',
            },
            {
                label: 'Nom interne',
                render: ({ name }: FormaliteDocument) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {name || '—'}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'name',
            },
            {
                label: 'Type',
                render: ({ type }: FormaliteDocument) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {type ? findLabelByDocTypeId(type) : '—'}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'type',
            },
            {
                label: 'Signable',
                render: ({ signable }: FormaliteDocument) => (
                    <Chip
                        color={signable ? 'success' : 'default'}
                        label={signable ? 'Oui' : 'Non'}
                    />
                ),
                isDisplayed: true,
                sortable: false,
            },
            {
                label: 'Actions',
                displayLabel: false,
                render: (item: FormaliteDocument) =>
                    item.id ? <DocumentTypeListItemActions item={item} /> : null,
                isDisplayed: true,
            },
        ],
        [],
    );

    const filters: FiltersDatatableList = [
        {
            label: 'Libellé',
            field: 'libelle',
            type: 'text',
        },
        {
            label: 'Nom interne',
            field: 'name',
            type: 'text',
        },
        {
            label: 'Type',
            field: 'type',
            type: 'staticSelect',
            options: documentTypes.map((documentType) => ({
                value: String(documentType.value),
                label: documentType.label,
            })),
            renderFilterList: (value) =>
                documentTypes.find((documentType) => String(documentType.value) === value)?.label ||
                '',
        },
    ];

    return datasource ? (
        <>
            <LazyBreadcrumb
                path={[
                    { path: 'Types de documents', link: '/types-de-documents/liste' },
                    'Liste des types de documents',
                ]}
            />
            <StoreDatatable
                dataSource={datasource}
                columns={columns}
                fetchData={getFormaliteDocuments}
                filters={filters}
                localStorageKey="DocumentTypesStoreDatatable"
                onClickRow={handleRowClick}
                onWheelClickRow={handleRowWheelClick}
            />
        </>
    ) : null;
};

export default DocumentTypesListing;
